import { url } from "inspector";
import { getMapThumbnail } from "../Clip";
import { ESupportedGames } from "MVVM/@types";

interface TBuildLink {
  url: string;
  game?: number | ESupportedGames;

  numClaimableClips?: number;
  username?: string;
  clipThumbnail?: string;
  bestClipId?: string | null;
  loggedInRedirect?: string;
  platform?: string | null;
}

export const buildLink = ({
  url,
  game,
  numClaimableClips,
  username,
  clipThumbnail,
  bestClipId,
  loggedInRedirect,
  platform,
}: TBuildLink) => {
  try {
    if (!window.rudderanalytics.getAnonymousId) return url;
    const anonymousId = window.rudderanalytics.getAnonymousId();
    if (!anonymousId) return url;
    const urlObject = new URL(url);
    urlObject.searchParams.append("ajs_aid", anonymousId);

    if (game) urlObject.searchParams.append("game", game.toString());
    if (numClaimableClips)
      urlObject.searchParams.append("clips", numClaimableClips.toString());
    if (username) urlObject.searchParams.append("username", username);
    if (clipThumbnail)
      urlObject.searchParams.append(
        "clipThumbnail",
        encodeURIComponent(clipThumbnail),
      );
    if (bestClipId) urlObject.searchParams.append("bestClipId", bestClipId);
    if (loggedInRedirect)
      urlObject.searchParams.append("loggedInRedirect", loggedInRedirect);
    if (platform) urlObject.searchParams.append("platform", platform);
    return urlObject.toString();
  } catch (e) {
    return url;
  }
};

export const getPartnerAnalyticsId = (partnerId: string) => {
  if (["leetify", "L202211"].includes(partnerId)) return "Leetify";
  if (["gamersclub", "GC2022"].includes(partnerId)) return "Gamersclub";
  if ("SCOPEGG2212" === partnerId) return "Scope.gg";
  if ("ESPORTAL23" === partnerId) return "Esportal";

  return partnerId;
};

export const isLegacyPartner = (partnerId: string) => {
  return [
    "leetify",
    "L202211",
    "gamersclub",
    "GC2022",
    "SCOPEGG2212",
    "ESPORTAL23",
    "FACEIT",
  ].includes(partnerId);
};

export const checkIfClipOwner = (clip: TClip, uid: string) => {
  if ([730, 7302].includes(clip.game)) return uid === clip?.steamId64;
  if ([101].includes(clip.game)) return uid === clip?.username;
  return false;
};

export const getUserDisplayName = (partnerViewSession: any) => {
  if (
    [730, 7302].includes(partnerViewSession?.clip?.game) &&
    partnerViewSession?.isOwnClip
  )
    return partnerViewSession?.clip?.username;
  if ([101].includes(partnerViewSession?.clip?.game))
    return partnerViewSession?.overlayConfig?.uid;
  return "";
};

export const getClaimableClipThumbnail = (clip: TClip) => {
  if (clip.clipImageThumb) return clip.clipImageThumb;

  return getMapThumbnail(clip?.mapName, true);
};

export const getBestClaimableClip = (
  clips: TClip[],
  partnerId: string | null,
) => {
  let clipsToSort = [...clips];

  if (partnerId) {
    clipsToSort = clipsToSort.filter(
      (clip) => clip.partner.partnerId.partnerId === partnerId,
    );
  }

  const sortedByAllstarScore = clipsToSort
    .filter((c) => c?.score?.value != null)
    .sort((a, b) => b.score.value - a.score.value);

  if (sortedByAllstarScore.length === 0) return null;

  return sortedByAllstarScore[0];
};

export const isFaceItPartner = (platformId: string) =>
  ["ESLFACEITGROUP", "FACEITLTD", "FACEIT"].includes(platformId);

export const downloadClipFile = async (clipUrl: string) => {
  try {
    const response = await fetch(clipUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = clipUrl.split("/").pop() || "clip.mp4";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(clipUrl, "_blank");
  }
};
